import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const STATUSES = ["pending", "assigned", "production", "ready", "delivered"];
const STATUS_COLORS = {
  pending: "text-muted-foreground",
  assigned: "text-blue-400",
  production: "text-orange-400",
  ready: "text-gold",
  delivered: "text-green-400",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      customer_id: "",
      design_name: "",
      description: "",
      weight: 0,
      advance_amount: 0,
      estimated_total: 0,
      delivery_date: "",
      design_image_url: "",
      reference_image_url: "",
      assigned_to: "",
      status: "pending",
    };
  }

  const load = async () => {
    const [o, c] = await Promise.all([
      api.get("/orders"),
      api.get("/customers"),
    ]);
    setOrders(o.data);
    setCustomers(c.data);
    try {
      const u = await api.get("/users");
      setUsers(u.data);
    } catch (e) {
      setUsers([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ ...blank(), ...o });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_id || !form.design_name) {
      toast.error("Customer and design name required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        weight: Number(form.weight) || 0,
        advance_amount: Number(form.advance_amount) || 0,
        estimated_total: Number(form.estimated_total) || 0,
      };
      [
        "delivery_date",
        "design_image_url",
        "reference_image_url",
        "assigned_to",
        "description",
      ].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      if (editing) await api.put(`/orders/${editing.id}`, payload);
      else await api.post("/orders", payload);
      toast.success(editing ? "Updated" : "Order created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="orders-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Bespoke
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Custom Orders
          </h1>
        </div>
        <Button
          data-testid="new-order-btn"
          onClick={openNew}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New order
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Design</TableHead>
              <TableHead className="text-right">Advance</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-10 text-muted-foreground"
                >
                  No orders yet.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-mono text-xs">
                    {o.order_no}
                  </TableCell>
                  <TableCell>{o.customer_name || "—"}</TableCell>
                  <TableCell>
                    <div>{o.design_name}</div>
                    {o.weight ? (
                      <div className="text-xs text-muted-foreground">
                        {o.weight} g
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(o.advance_amount)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {o.delivery_date || "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs uppercase tracking-wider ${STATUS_COLORS[o.status]}`}
                    >
                      {o.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => openEdit(o)}
                      className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                    >
                      <PencilSimple size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit" : "New"} Order
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Customer
              </Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v })}
              >
                <SelectTrigger
                  data-testid="order-customer"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.mobile}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Status
              </Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger
                  data-testid="order-status"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Design name *
              </Label>
              <Input
                data-testid="order-design"
                className="mt-1.5 bg-background border-border"
                value={form.design_name}
                onChange={(e) =>
                  setForm({ ...form, design_name: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Description
              </Label>
              <Input
                className="mt-1.5 bg-background border-border"
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <Field
              label="Weight (g)"
              type="number"
              value={form.weight}
              onChange={(v) => setForm({ ...form, weight: v })}
            />
            <Field
              label="Estimated total (₹)"
              type="number"
              value={form.estimated_total}
              onChange={(v) => setForm({ ...form, estimated_total: v })}
            />
            <Field
              label="Advance (₹)"
              type="number"
              value={form.advance_amount}
              onChange={(v) => setForm({ ...form, advance_amount: v })}
            />
            <Field
              label="Delivery date"
              type="date"
              value={form.delivery_date}
              onChange={(v) => setForm({ ...form, delivery_date: v })}
            />
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImageUpload
                testid="order-design-image"
                label="Design image"
                value={form.design_image_url}
                onChange={(v) => setForm({ ...form, design_image_url: v })}
              />
              <ImageUpload
                testid="order-reference-image"
                label="Reference image"
                value={form.reference_image_url}
                onChange={(v) => setForm({ ...form, reference_image_url: v })}
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Assigned to (Karigar / user)
              </Label>
              <Select
                value={form.assigned_to || "_none_"}
                onValueChange={(v) =>
                  setForm({ ...form, assigned_to: v === "_none_" ? "" : v })
                }
              >
                <SelectTrigger className="mt-1.5 bg-background border-border">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} · {u.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-order-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </Label>
    <Input
      type={type}
      className="mt-1.5 bg-background border-border"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
